import { useId, useState } from "react";
import type { SxProps, Theme } from "@mui/material/styles";

import { Box } from "ui/box";
import { Button } from "ui/button";
import { Collapse } from "ui/collapse";
import { Typography } from "ui/typography";

import type { TouchpointAttributes } from "hooks/api/use-touchpoints";

import { formatFiscalYear, getDisplayTouchpointIcon } from "utilities";

import Icons from "assets";

type RollToNextFYSelectedItemsProps = {
  items: TouchpointAttributes[];
};

const TOGGLE_SX: SxProps<Theme> = { alignSelf: "flex-start", minWidth: 0, px: 0 };

const LIST_SX: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
  m: 0,
  mt: 1,
  p: 0,
  listStyle: "none",
  maxHeight: 112,
  overflowY: "auto",
};

const ITEM_SX: SxProps<Theme> = { display: "flex", alignItems: "center", gap: 1 };

const FISCAL_YEAR_SX: SxProps<Theme> = (theme) => ({ color: theme.palette.text.secondary });

export const RollToNextFYSelectedItems = ({ items }: RollToNextFYSelectedItemsProps) => {
  const listId = useId();
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = () => setIsExpanded((expanded) => !expanded);

  return (
    <Box>
      <Button
        variant="text"
        onClick={handleToggle}
        aria-expanded={isExpanded}
        aria-controls={listId}
        endIcon={isExpanded ? <Icons.ChevronDown /> : <Icons.ChevronRight />}
        sx={TOGGLE_SX}>
        Selected items
      </Button>
      <Collapse in={isExpanded}>
        <Box component="ul" id={listId} sx={LIST_SX}>
          {items.map((item) => (
            <Box component="li" key={item.id} sx={ITEM_SX}>
              {getDisplayTouchpointIcon(item.type)}
              <Typography component="span" variant="body2">
                {item.name}
              </Typography>
              <Typography component="span" variant="body2" sx={FISCAL_YEAR_SX}>
                {formatFiscalYear(item.latestVersion?.fiscalYear)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};
